import { Component, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest, fromEvent, merge, of, Subject, Subscription } from 'rxjs';
import { distinctUntilKeyChanged, filter, map, take, withLatestFrom } from 'rxjs/operators';

import { ArrayFilterPipe } from './pipes/array-filter.pipe';
import { PartsService } from './services/parts.service';
import { addToBasket } from './store/actions/basket.actions';
import { hoverParts, openBasket, updatePage } from './store/actions/config.actions';
import { CONFIG } from './store/reducer/config.reducer';
import { DATA } from './store/reducer/data.reducer';

declare var window: any;
@Component({
  selector: 'parts-view',
  templateUrl: './parts-view.component.html',
  styleUrls: ['./parts-view.component.scss'],
  providers: [ArrayFilterPipe],
  standalone: false
})
export class PartsViewComponent implements OnInit, OnDestroy {
  // the catalog id
  @Input() uid: string;

  @Input() machineSerial: string;

  @Input() pageId: string;

  @Output() pageIdChange = new EventEmitter();
  @Output() basketChange = new EventEmitter();

  backgroundLoadDone = false;
  isRouterActive = false;
  private routerSubject: Subject<{ ident: string; selectedPartId: string }> = new Subject<{
    ident: string;
    selectedPartId: string;
  }>();
  private hoverCurrentPageNumber$ = new Subject<string>();

  private networkStatus: boolean = false;
  private networkStatus$: Subscription = Subscription.EMPTY;

  constructor(
    private store: Store<{ data: DATA; config: CONFIG; basket: [] }>,
    private arrayFilterPipe: ArrayFilterPipe,
    private ngZone: NgZone,
    private partsService: PartsService
  ) {
    window.routerSubject = this.routerSubject;
    window.hoverCurrentPageNumber$ = this.hoverCurrentPageNumber$;
  }

  ngOnInit() {
    this.checkNetworkStatus();

    // save the machine serial number to reuse it in svgLoad.js
    window['machineSerial'] = this.machineSerial;

    this.store
      .select('basket')
      .pipe(
        withLatestFrom(this.store.select('data')),
        map(([basket, data]) => {
          const _basket = [];
          // basket's type is [catalogId]: basket for this catalog
          // each basket is [pageId]: items for this page
          // each item is [itemId]: {count: number, actualItem: DATA_VALUES['@attributes']}
          if (basket[this.uid]) {
            Object.keys(basket[this.uid]).map((pageId: string) => {
              const itemIds = Object.keys(basket[this.uid][pageId]);
              Object.keys(itemIds).map((itemIndex: string) => {
                const itemId = itemIds[itemIndex];
                const { count, actualItem } = basket[this.uid][pageId][itemId];
                let name = this.tryGuessName(actualItem);
                let pageIdent = undefined;
                data?.pages?.forEach((d: any) => {
                  d?.page?.forEach((p) => {
                    const attrs = p?.['@attributes'];
                    if (!attrs) return;
                    if (attrs['numpage'] == pageId) {
                      pageIdent = attrs['ident'];
                    }
                  });
                });
                if (pageIdent === undefined) {
                  console.warn('unable to find page ident for item', actualItem['numident']);
                } else {
                  console.log('using page ident', pageIdent, 'for item', actualItem['numident']);
                }
                _basket.push({
                  parts: this.tryGuessBsaNumber(actualItem['numbobst'], actualItem['parts']),
                  desc: name,
                  quantity: count,
                  machine: this.machineSerial,
                  page: actualItem['numident'],
                  position: typeof actualItem['id'] === 'string' ? actualItem['id'] : '0',
                  name: name,
                  pageIdent,
                });
              });
            });
          }
          return _basket;
        })
      )
      .subscribe((basket) => {
        this.basketChange.emit(basket);
      });

    this.store
      .select('config')
      .pipe()
      .pipe(
        distinctUntilKeyChanged('currentPageNumber'),
        filter(({ currentPageNumber }) => currentPageNumber >= 0)
      )
      .subscribe((config: CONFIG) => {
        this.pageId = `${config.currentPageNumber}`;
        this.pageIdChange.emit(this.pageId);
      });

    window.hoverCurrentPageNumber$.subscribe((currentHoveredNumber) => {
      this.ngZone.run(() => {
        this.store.dispatch(hoverParts(currentHoveredNumber));
      });
    });

    let selectedNumPage;
    this.routerSubject.subscribe(({ ident, selectedPartId }) => {
      this.ngZone.run(() => {
        if (selectedPartId) {
          this.store.dispatch(openBasket());
        }
        if (ident) {
          combineLatest([this.store.select('data'), this.store.select('config')])
            .pipe(take(1))
            .subscribe((response) => {
              if (ident.includes('page')) {
                const page = ident.match(/\d+/g) && ident.match(/\d+/g)[0];
                if (page) {
                  this.store.dispatch(updatePage(Number(page)));
                  return;
                }
              }
              response[0].pages.map((d: any) => {
                const resultData = this.arrayFilterPipe.transform(d.page, ident, 'ident');
                if (resultData.length > 0) {
                  selectedNumPage = resultData[0]['@attributes']['numpage'];
                }
              });
              this.store.dispatch(updatePage(Number(selectedNumPage)));
            });
        } else {
          combineLatest([this.store.select('data'), this.store.select('config')])
            .pipe(take(1))
            .subscribe((response) => {
              const data = response[0];
              const uid = response[1]['uid'];
              const currentPageNumber = response[1]['currentPageNumber'];
              let actualItem;

              data.pages.forEach((d: any) => {
                d.page.forEach((page) => {
                  if (page['@attributes']['numpage'] == currentPageNumber && page.piece) {
                    actualItem = this.findActualItem(page, selectedPartId);
                  }
                });
              });

              let strTooltip = '';
              const svgElt =
                document.querySelector(`[itemnumber="${selectedPartId}"]`) ??
                document.querySelector(`[onclick="javascript:parent.vue_piece(${selectedPartId})"]`);
              if (!svgElt) {
                console.error('unable to find node with itemnumber', selectedPartId, actualItem);
                return;
              } else {
                strTooltip = svgElt.getAttribute('strTooltip');
              }
              this.store.dispatch(
                addToBasket(uid, currentPageNumber, selectedPartId, {
                  ...actualItem,
                  parts: strTooltip
                })
              );
            });
        }
      });
    });
  }

  tryGuessBsaNumber(numBobst: string, parts: string) {
    // we need to find the id of the piece in the form BSA1234567890
    // but some xml and svgs are not consistent. sometimes the id is found in the xml <numbobst>XXX</numbobst>
    // sometimes it is found in the svg ShowToolTip(evt,'23',&quot;BSA0656139000&quot;)
    if (this.isValidBsaNumber(numBobst)) return numBobst;
    // some pieces have <numbobst>1114 0169 00</numbobst>
    const regex = /^\d{4} \d{4} \d{2}$/;
    if (numBobst && regex.test(numBobst)) return 'BSA' + numBobst.replace(/ /g, '');

    // parts is the value of the strTooltip attribute in the svg
    const startOfToolTip = parts ? parts.trim().split(' ')[0] : '';
    if (this.isValidBsaNumber(startOfToolTip)) return startOfToolTip;

    console.warn('unable to guess bsa number', numBobst, parts);
    return numBobst ?? parts ?? '';
  }

  isValidBsaNumber(bsaNumber: string) {
    // there is no defined format for bsa number, but usually it starts with BSA or SAM followed by alphanumeric characters
    // this could be improved if we have more precise rules
    if (!bsaNumber) return false;
    const regex = /^(BSA|SAM)[a-z0-9]/i;
    return regex.test(bsaNumber);
  }

  tryGuessName(actualItem: any) {
    // if node is empty, xmlToJson set its value to {}
    let name = actualItem['desan'];
    if (name && typeof name === 'string') {
      return name;
    }
    name = actualItem['desfr'];
    if (name && typeof name === 'string') {
      return name;
    }
    name = actualItem['desal'];
    if (name && typeof name === 'string') {
      return name;
    }
    return actualItem['numident'];
  }

  private findActualItem(page: any, selectedPartId: string) {
    // some xml have a property <val>0003 / 0005</val> which means that the piece is valid for machine 3 to 5
    // the validity number is found in the machine serial number : BSA057003705 has validity 037
    if (!Array.isArray(page.piece)) {
      return page.piece;
    }
    let pieces = page.piece.filter((p) => p.id == selectedPartId);
    if (pieces.length === 0) {
      pieces = page.piece.filter((p) => p.id == selectedPartId + '.1');
    }
    if (pieces.length > 1) {
      let machineValidity = this.getMachineValidity();
      console.log('multiple pieces found for id', selectedPartId, pieces, machineValidity);
      if (machineValidity) {
        let validPiece = pieces.find((p) => this.isPieceValid(p, machineValidity));
        if (validPiece) {
          console.log('valid piece', validPiece);
          return validPiece;
        }
      }
    }
    return pieces[0];
  }

  isPieceValid(piece: any, machineValidity: number) {
    if (!piece || !piece.val) {
      return false;
    }
    const minMax = piece.val.split('/');
    if (minMax.length === 1 || !minMax[1].trim()) {
      return machineValidity >= Number(minMax[0]);
    } else if (minMax.length === 2) {
      return machineValidity >= Number(minMax[0]) && machineValidity <= Number(minMax[1]);
    } else {
      return false;
    }
  }

  private getMachineValidity(): number {
    return this.machineSerial && this.machineSerial.length === 12 ?
      Number(this.machineSerial.slice(7, 10)) : 0;
  }

  ngOnDestroy() {
    this.routerSubject.unsubscribe();
    this.networkStatus$.unsubscribe();
  }

  onActivate() {
    this.isRouterActive = true;
  }

  onDeactivate() {
    this.isRouterActive = false;
  }

  private checkNetworkStatus() {
    this.networkStatus = navigator.onLine;
    this.networkStatus$ = merge(of(null), fromEvent(window, 'online'), fromEvent(window, 'offline'))
      .pipe(map(() => navigator.onLine))
      .subscribe((status: any) => {
        this.networkStatus = status;
      });
  }
}
